import React, { useEffect, useRef, useCallback } from 'react';
import L from 'leaflet';
import { getLatLng, getEventDate, isOpen } from '../api/eonet';
import './FireMap.css';

const FIRE_COLOR = '#D85A30';
const RESOLVED_COLOR = '#5F5E5A';

export default function FireMap({ events, selectedId, mapMode, onSelectEvent, flyTo }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const layersRef = useRef([]);

  // Initialize map once
  useEffect(() => {
    if (mapInstanceRef.current) return;

    const map = L.map(mapRef.current, {
      center: [20, 0],
      zoom: 2,
      zoomControl: true,
      scrollWheelZoom: true,
    });

    L.tileLayer(
      'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
      {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 20,
      }
    ).addTo(map);

    mapInstanceRef.current = map;
  }, []);

  // Fly to selected event
  useEffect(() => {
    if (!flyTo || !mapInstanceRef.current) return;
    mapInstanceRef.current.flyTo(flyTo, 7, { duration: 1.2 });
  }, [flyTo]);

  // Render layers whenever events or mapMode changes
  const renderLayers = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Remove old layers
    layersRef.current.forEach(l => map.removeLayer(l));
    layersRef.current = [];

    events.forEach(event => {
      const coords = getLatLng(event);
      if (!coords) return;

      const open = isOpen(event);
      const color = open ? FIRE_COLOR : RESOLVED_COLOR;
      const dateStr = getEventDate(event);
      const source = event.sources?.[0]?.id || 'NASA EONET';

      const popupContent = `
        <div class="fire-popup">
          <div class="fire-popup-title">${event.title}</div>
          <div class="fire-popup-status" style="color:${color}">${open ? '● ACTIVE' : '○ RESOLVED'}</div>
          <div class="fire-popup-meta">${dateStr}</div>
          <div class="fire-popup-meta">Source: ${source}</div>
          <div class="fire-popup-coords">${coords[0].toFixed(4)}°, ${coords[1].toFixed(4)}°</div>
        </div>
      `;

      let layer;

      if (mapMode === 'markers') {
        const icon = L.divIcon({
          html: `<div class="fire-marker${open ? '' : ' fire-marker-closed'}"></div>`,
          className: '',
          iconSize: [12, 12],
          iconAnchor: [6, 6],
        });
        layer = L.marker(coords, { icon }).bindPopup(popupContent);
      } else {
        const radius = open ? 65000 : 38000;
        layer = L.circle(coords, {
          radius,
          color,
          fillColor: color,
          fillOpacity: open ? 0.3 : 0.1,
          weight: open ? 1.5 : 0.8,
          opacity: open ? 0.75 : 0.35,
        }).bindPopup(popupContent);
      }

      layer.on('click', () => onSelectEvent(event.id));
      layer.addTo(map);
      layersRef.current.push(layer);
    });
  }, [events, mapMode, onSelectEvent]);

  useEffect(() => {
    renderLayers();
  }, [renderLayers]);

  return <div ref={mapRef} className="fire-map" />;
}
